<template>
  <Teleport to="body">
    <SlotPanel
      v-if="hasPerformanceUpdates || state === STATE.SAVED"
      class="flex justify-center gap-8 items-baseline w-full bg-surface-primary py-4 sticky bottom-0 border-t border-linear-subtle h-16 text-fg-primary"
    >
      <template v-if="state === STATE.INITIAL">
        <UpdateIndicator id="message" status="updated">
          <span class="text-fg-primary">You have unsaved changes.</span>
        </UpdateIndicator>
        <SlotButton id="discard-all-button" alt @click="setState(STATE.CONFIRM_DISCARD)">
          Discard All
        </SlotButton>
        <SlotButton id="save-changes-button" @click="saveUpdates"> Save Changes </SlotButton>
      </template>
      <template v-else-if="state === STATE.CONFIRM_DISCARD">
        <span id="confirm-discard-message">Are you sure you want to discard all changes?</span>
        <SlotButton id="confirm-discard-button" altWarning @click="discardUpdates">
          Discard
        </SlotButton>
        <SlotButton id="save-button" @click="saveUpdates"> Save </SlotButton>
      </template>
      <template v-else-if="state === STATE.SAVING">
        <span id="saving-message"> Saving changes... </span>
      </template>
      <template v-else-if="state === STATE.SAVED">
        <span id="saved-message"> Changes saved! </span>
      </template>
      <template v-else-if="state === STATE.ERROR">
        <span id="error-message" class="text-alerts-warning">
          Error saving changes. Try again.
        </span>
        <SlotButton id="error-discard-button" altWarning @click="discardUpdates">
          Discard
        </SlotButton>
        <SlotButton id="error-save-button" @click="saveUpdates"> Save Again </SlotButton>
      </template>
    </SlotPanel>
  </Teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'

import SlotButton from '@/common/components/atoms/SlotButton.vue'
import UpdateIndicator from '@/common/components/atoms/UpdateIndicator.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import { usePerformanceUpdateStore } from '@/modules/performance/stores/PerformanceUpdateStore'

type State = (typeof STATE)[keyof typeof STATE]

const STATE = {
  INITIAL: 'initial',
  CONFIRM_DISCARD: 'confirm-discard',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
}
const state = ref<State>(STATE.INITIAL)

const performanceUpdateStore = usePerformanceUpdateStore()
const { hasPerformanceUpdates } = storeToRefs(performanceUpdateStore)

function setState(newState: State) {
  state.value = newState
}

function discardUpdates() {
  performanceUpdateStore.discardUpdates()
  setState(STATE.INITIAL)
}
async function saveUpdates() {
  try {
    setState(STATE.SAVING)
    await performanceUpdateStore.saveUpdates()
    setState(STATE.SAVED)
    setTimeout(() => {
      setState(STATE.INITIAL)
    }, 4000)
  } catch (error) {
    setState(STATE.ERROR)
  }
}

watch(hasPerformanceUpdates, hasUpdates => {
  if (!hasUpdates && state.value !== STATE.SAVED) {
    setState(STATE.INITIAL)
  }
})
</script>
